import _ from 'lodash';
import * as yup from 'yup';

import { MUST_BE_GREATER_THAN_ZERO_TEXT, REQUIRED_TEXT } from '@/common/constants';

interface PreviousTrigger {
  key: string;
  name: string;
}

interface TriggerValidatorParams {
  previousTriggers?: PreviousTrigger[];
  maxTrigger?: PreviousTrigger;
}

export const createTriggerAmountValidator = (params: TriggerValidatorParams) => {
  const { previousTriggers = [], maxTrigger } = params;

  return yup.string().test({
    test(val) {
      // 1. Required check
      if (!val) {
        return this.createError({ message: REQUIRED_TEXT });
      }

      const numVal = _.toNumber(val);
      const parent = this.parent as Record<string, string>;

      // 2. Must be greater than $0
      if (_.isNaN(numVal) || numVal <= 0) {
        return this.createError({ message: MUST_BE_GREATER_THAN_ZERO_TEXT });
      }

      // 3. Cross-tier: must be bigger than closest filled previous trigger of same type
      if (previousTriggers.length > 0) {
        for (let i = previousTriggers.length - 1; i >= 0; i--) {
          const prevValue = parent[previousTriggers[i].key];
          if (!_.isEmpty(prevValue)) {
            if (numVal <= _.toNumber(prevValue)) {
              return this.createError({
                message: `Should be bigger than ${previousTriggers[i].name}`,
              });
            }
            break;
          }
        }
      }

      // 4. Within-tier: embedded must be <= family
      if (maxTrigger) {
        const maxValue = parent[maxTrigger.key];
        if (!_.isEmpty(maxValue) && numVal > _.toNumber(maxValue)) {
          return this.createError({
            message: `Should be equal or less than ${maxTrigger.name}`,
          });
        }
      }

      return true;
    },
  });
};

import { CoverageLevelPayload, CUSTOM_TRIGGER_AMOUNT_PREFIX } from './CoverageLevels.constants';

/**
 * Splits flat form values into structured payload with customTriggerAmounts.
 * Form returns flat keys like { customTrigger_1048: '500', coverageCode: 'F' }
 * This groups trigger fields into customTriggerAmounts object.
 */
export const splitTriggerAmountsFromValues = (
  values: Record<string, unknown>
): Partial<CoverageLevelPayload> => {
  const customTriggerAmounts: Record<string, string> = {};
  const rest: Record<string, unknown> = {};

  Object.entries(values).forEach(([key, val]) => {
    if (key.startsWith(CUSTOM_TRIGGER_AMOUNT_PREFIX)) {
      customTriggerAmounts[key] = val as string;
    } else {
      rest[key] = val;
    }
  });

  return { ...rest, customTriggerAmounts } as Partial<CoverageLevelPayload>;
};

interface TriggerConfig {
  trigger_id?: number | null;
  threshold_amount?: number | null;
}

/**
 * Converts backend trigger_configs into customTriggerAmounts format for the form.
 * { trigger_id: 1048, threshold_amount: 500 } → { customTrigger_1048: '500' }
 */
export const mapTriggerConfigsToAmounts = (triggerConfigs?: TriggerConfig[]): Record<string, string> => {
  const result: Record<string, string> = {};
  if (!triggerConfigs) return result;

  triggerConfigs.forEach((tc) => {
    if (tc.trigger_id != null && tc.threshold_amount != null) {
      result[`${CUSTOM_TRIGGER_AMOUNT_PREFIX}${tc.trigger_id}`] = _.toString(tc.threshold_amount);
    }
  });

  return result;
};
